"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionModelDrawableVerticalSeparation = void 0;
const __1 = require("../..");
const theme_1 = require("../../../draw/theme");
const id_1 = require("../../../id");
const fetchable_1 = require("../../../rest/fetchable");
const unit_1 = require("../../../unit");
const drawable_1 = require("./drawable");
/**
 * Draw a vertical separation annotation with a label and an optional minimum distance check.
 */
class CollectionModelDrawableVerticalSeparation extends drawable_1.CollectionModelDrawable {
    /**
     * @throws IkeFetchableRaceError if the capture fails to load due to a race condition.
     */
    static createWithLabel(drawableState, topMarkerId, bottomMarkerId, fieldId) {
        const id = id_1.IkeId.generate();
        const label = new __1.CollectionModelDrawableLabel(drawableState, `${id}:label`, 0, 0, id);
        const drawable = new CollectionModelDrawableVerticalSeparation(drawableState, topMarkerId, bottomMarkerId, fieldId, label, id);
        try {
            drawable.moveLabelToDefaultPosition();
        }
        catch (error) {
            if (error instanceof fetchable_1.IkeFetchableRaceError) {
                // TODO: Add logging to IkeModelCollectionReader and log a warning here.
                // Ignore the error and return the new drawable as usual so that the drawable state
                // is not affected by the race condition.
                // The label will be in the wrong position but at least the state will be correct otherwise.
                return drawable;
            }
            throw error;
        }
        return drawable;
    }
    constructor(collectionModelDrawableState, topMarkerId, bottomMarkerId, fieldId, label, drawableId = id_1.IkeId.generate()) {
        super(collectionModelDrawableState, drawableId);
        this._topMarkerId = topMarkerId;
        this._bottomMarkerId = bottomMarkerId;
        this._fieldId = fieldId;
        this._label = label;
    }
    get label() {
        return this._label;
    }
    getLabelConnectionPoint(options) {
        return this.getLabelConnectionPointForSide(this.label.isOnLeftSide(), options.draw.state.zoom);
    }
    /**
     * Pass leftSide = true for left, or leftSide = false for right.
     * @throws IkeFetchableRaceError if the capture fails to load due to a race condition.
     */
    getLabelConnectionPointForSide(leftSide, zoom = 1) {
        const indexOffset = this.indexInGroupWithSameTopMarker() * theme_1.StyleConfig.STROKE_WIDTH * 4;
        const markerOffsetWidth = 200 - zoom * 10;
        return {
            // If label is on left side, draw line to middle of vertical separation on the left side of the pole.
            // Otherwise, draw to middle of the separation on the right side of the pole.
            x: leftSide
                ? this.topMarker.x - markerOffsetWidth + indexOffset
                : this.topMarker.x + markerOffsetWidth - indexOffset,
            y: this.topMarker.y + (this.distanceBetweenMarkersInPixels + theme_1.StyleConfig.STROKE_WIDTH) / 2,
        };
    }
    get topMarker() {
        return this.drawableState.getDrawable(this._topMarkerId);
    }
    get bottomMarker() {
        return this.drawableState.getDrawable(this._bottomMarkerId);
    }
    get topMarkerId() {
        return this._topMarkerId;
    }
    get bottomMarkerId() {
        return this._bottomMarkerId;
    }
    get distanceBetweenMarkersInPixels() {
        return this.bottomMarker.y - this.topMarker.y;
    }
    get distanceBetweenMarkersInMeters() {
        return this.drawableState.collection.state
            .getGeometry(this.drawableState.captureId)
            .calculateDistance(this.bottomMarker.getVector(), this.topMarker.getVector());
    }
    /**
     * @throws IkeFetchableRaceError if the capture fails to load due to a race condition.
     */
    verticalSeparationsWithSameTopMarker() {
        const result = [];
        for (const drawable of this.drawableState.getAllDrawables()) {
            if (!(drawable instanceof CollectionModelDrawableVerticalSeparation)) {
                continue;
            }
            if (drawable.topMarkerId !== this.topMarkerId) {
                continue;
            }
            result.push(drawable);
        }
        result.sort((a, b) => { var _a, _b; return ((_a = a.field.value) !== null && _a !== void 0 ? _a : 0) - ((_b = b.field.value) !== null && _b !== void 0 ? _b : 0); });
        return result;
    }
    /**
     * Returns the index of this vertical separation in the list of vertical separations
     * that have the same top marker, when ordered by field value (distance between top and bottom markers).
     * @throws IkeFetchableRaceError if the capture fails to load due to a race condition.
     */
    indexInGroupWithSameTopMarker() {
        return this.verticalSeparationsWithSameTopMarker().indexOf(this);
    }
    get fieldId() {
        return this._fieldId;
    }
    get field() {
        return this.collection.getFieldDeep(this._fieldId);
    }
    get clearanceThresholdInMeters() {
        return this.field.schema.clearanceThresholdInMeters;
    }
    /**
     * Returns true if the distance between the top and bottom markers is greater than the clearance defined on the form field.
     * If a clearance is not defined on the form field, it returns undefined.
     */
    get isInClearance() {
        if (!this.clearanceThresholdInMeters) {
            return undefined;
        }
        return this.distanceBetweenMarkersInMeters > this.clearanceThresholdInMeters;
    }
    /**
     * @deprecated Use {@link getLabelDisplayText} instead.
     */
    get text() {
        return this.field.schema.displayName;
    }
    getLabelDisplayText(options) {
        // The distance between the markers formatted as a distance string (e.g. 66" or 52cm).
        const clearanceText = unit_1.IkeUnit.display(this.distanceBetweenMarkersInMeters, options.units === 'meters' ? unit_1.IkeUnit.CENTIMETER : unit_1.IkeUnit.INCH, undefined, undefined, 0);
        if (!this.clearanceThresholdInMeters) {
            return [clearanceText + ' - ' + this.field.schema.displayName];
        }
        return [(this.isInClearance ? '✅ ' : '❌ ') + clearanceText + ' - ' + this.field.schema.displayName];
    }
    get x() {
        return this.topMarker.x;
    }
    get y() {
        return this.topMarker.y;
    }
    /**
     * @throws IkeFetchableRaceError if the capture fails to load due to a race condition.
     */
    moveLabelToDefaultPosition() {
        this.label.snapToLeftSide();
        this.label.move(this.label.x, this.getLabelConnectionPointForSide(true, 1).y);
    }
    remove() {
        super.remove();
        // Remove the label too, unless it has already been removed
        if (this.drawableState.hasDrawable(this._label.id)) {
            this.label.remove();
        }
    }
    get isInsightMeasurement() {
        return false;
    }
    isApprovableInsightDrawable() {
        return false;
    }
    get isVisible() {
        return true;
    }
    clone(newParentDrawableState) {
        if (!newParentDrawableState.hasDrawable(this._label.id)) {
            this.label.clone(newParentDrawableState);
        }
        return new CollectionModelDrawableVerticalSeparation(newParentDrawableState, this._topMarkerId, this._bottomMarkerId, this._fieldId, newParentDrawableState.getDrawable(this.label.id), this.id);
    }
    equals(other) {
        return (other instanceof CollectionModelDrawableVerticalSeparation &&
            this._topMarkerId === other._topMarkerId &&
            this._bottomMarkerId === other._bottomMarkerId &&
            this._fieldId === other._fieldId &&
            this._equals(other));
    }
}
exports.CollectionModelDrawableVerticalSeparation = CollectionModelDrawableVerticalSeparation;
//# sourceMappingURL=vertical.separation.drawable.js.map